import { SITE_VOICE } from '../siteVoice';

export type AlternateExplainerStep = 'what' | 'why' | 'how' | 'apps';

export const alternateScreenExplainerSteps = {
  what: {
    title: 'What Is the Alternate Screen?',
    description:
      'Terminals have two screen buffers: the normal screen (with your scrollback history) and the alternate screen (a separate canvas). Programs can switch between them. When they exit, your original screen reappears.',
  },
  why: {
    title: 'Why Does This Exist?',
    description:
      "Without the alternate screen, full-screen apps like vim would draw all over your terminal history. When you'd quit, you'd see vim's last screen mixed with your old output. The alternate screen keeps your scrollback clean.",
  },
  how: {
    title: 'How It Works',
    description:
      'Programs send an escape sequence to enter alternate screen mode: ^[[?1049h. When they exit, they send ^[[?1049l to return to the normal screen. The terminal swaps buffers, preserving your history.',
  },
  apps: {
    title: 'Apps That Use It',
    description:
      "vim, less, man, htop, tmux, and most TUI apps use the alternate screen. When you quit these apps, notice how your previous terminal output reappears? That's the alternate screen at work.",
  },
} satisfies Record<
  AlternateExplainerStep,
  { title: string; description: string }
>;

export const alternateScreenNormalContent = [
  `${SITE_VOICE.promptPath} $ ls -la`,
  'total 32',
  `drwxr-xr-x  5 ${SITE_VOICE.lsOwnerGroup}  160 Jan  8 10:30 .`,
  `drwxr-xr-x  8 ${SITE_VOICE.lsOwnerGroup}  256 Jan  7 14:22 ..`,
  `-rw-r--r--  1 ${SITE_VOICE.lsOwnerGroup}  847 Jan  8 10:30 package.json`,
  `-rw-r--r--  1 ${SITE_VOICE.lsOwnerGroup} 1205 Jan  8 10:28 README.md`,
  `drwxr-xr-x 12 ${SITE_VOICE.lsOwnerGroup}  384 Jan  8 10:30 src`,
  `${SITE_VOICE.promptPath} $ git status`,
  'On branch main',
  'nothing to commit, working tree clean',
  `${SITE_VOICE.promptPath} $ vim README.md`,
] as const;

export const alternateScreenVimBody = [
  SITE_VOICE.readmeHeading,
  '',
  'Notes while I drafted this interactive guide.',
  '',
  '## Ship it locally',
  '',
  '```bash',
  'bun install',
  'bun dev',
  '```',
  '',
  '~',
  '~',
  '~',
] as const;

export const alternateScreenDemoUi = {
  openVim: 'Open vim',
  exitVim: 'Exit vim (:q)',
  bufferNormal: 'Normal Buffer',
  bufferAlternate: 'Alternate Buffer',
  bufferHistorySubtitle: '(your history)',
  bufferCanvasSubtitle: "(app's canvas)",
  subsectionEscapes: 'The Escape Sequences',
  enterAltTitle: 'Enter Alternate Screen',
  enterAltEsc: '^[[?1049h',
  enterAltDesc:
    'Saves the current screen, clears display, and switches to the alternate buffer. Sent when opening vim, less, htop, etc.',
  exitAltTitle: 'Exit Alternate Screen',
  exitAltEsc: '^[[?1049l',
  exitAltDesc:
    'Restores the saved screen buffer. Your previous terminal content reappears exactly as it was. Sent when quitting the app.',
  layoutTitle: 'Buffer Layout',
  layoutCaption:
    'Only one buffer is visible at a time. The other is preserved in memory.',
  withoutAltTitle: 'What Would Happen Without It?',
  withoutAltHeading: 'Without Alternate Screen',
  withAltHeading: 'With Alternate Screen',
  withoutAltMessyFooter: `← My vim scratch buffer would weld itself into shell history.`,
  withAltFooter: '← Your history is intact!',
  vimOpensComment: '(vim opens on alternate screen...)',
  vimQuitComment: '(you edit, then :q...)',
  monoFileListing: 'build.ts vercel.json',
  monoLs: '$ ls',
  monoVimReadme: '$ vim README.md',
  monoMarkdownTitle: SITE_VOICE.readmeHeading,
  monoTilde: '~',
  monoInsertBanner: '-- INSERT --',
  monoQuitEllipsis: '... (quit vim)',
  vimBanner: 'README.md [+]',
  vimStatusLeft: '-- INSERT --',
  vimStatusRight: '1,1 All',
} as const;
